/**
 * Analyzes theme data to extract all font families and their required weights/styles
 * Includes fonts from tokens, elements, selected states, focus states, and style overrides
 */

/**
 * Extract font information from a style object
 * @param {Object} styles - Style object that may contain font properties
 * @returns {Object} Font information { fontFamily, fontWeight, isItalic }
 */
const extractFontFromStyles = styles => {
  if (!styles || typeof styles !== 'object') return null;

  const fontInfo = {};

  // Extract font family
  if (styles.fontFamily && styles.fontFamily !== 'default') {
    fontInfo.fontFamily = styles.fontFamily;
  }

  // Extract font weight
  if (styles.fontWeight && styles.fontWeight !== 0 && styles.fontWeight !== '0') {
    fontInfo.fontWeight = parseInt(styles.fontWeight, 10);
  }

  // Check for italic in font style or specific italic properties
  if (styles.fontStyle === 'italic' || styles.italic === true) {
    fontInfo.isItalic = true;
  }

  return Object.keys(fontInfo).length > 0 ? fontInfo : null;
};

/**
 * Determines the inherited font family based on element path
 * @param {string} path - Element path (e.g., 'elements.product.title')
 * @param {Object} tokens - Theme tokens containing font definitions
 * @returns {string|null} The inherited font family
 */
const getInheritedFontFamily = (path, tokens) => {
  // Check if this is a heading element
  const headingElements = [
    'title',
    'name',
    'heading',
    'headings',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
  ];

  // Check if this is a body text element
  /*   const bodyElements = [
    'description',
    'body',
    'text',
    'price',
    'label',
    'instructions',
    'quotes',
    'links',
  ]; */

  // Check path segments for heading indicators
  const pathSegments = path.toLowerCase().split('.');
  const isHeading = pathSegments.some(segment => headingElements.includes(segment));
  // const isBody = pathSegments.some(segment => bodyElements.includes(segment));

  // If explicitly a heading element, use heading font
  if (isHeading && tokens.fontHeading?.family && tokens.fontHeading.family !== 'default') {
    return tokens.fontHeading.family;
  }

  // Otherwise default to body font
  if (tokens.fontBody?.family && tokens.fontBody.family !== 'default') {
    return tokens.fontBody.family;
  }

  return null;
};

/**
 * Recursively scan an element object for font families
 * @param {Object} element - Element object to scan
 * @param {Set} fontData - Set to collect font data
 * @param {string} path - Current path for debugging
 * @param {Object} tokens - Theme tokens for font inheritance
 */
const scanElementForFonts = (element, fontData, path = '', tokens = {}) => {
  if (!element || typeof element !== 'object') return;

  // Helper function to process font info with inheritance
  const processFontInfo = (fontInfo, stylePath) => {
    if (!fontInfo) return;

    // If we have font weight or style but no family, inherit from parent context
    if (!fontInfo.fontFamily && (fontInfo.fontWeight || fontInfo.isItalic)) {
      const inheritedFamily = getInheritedFontFamily(stylePath, tokens);
      if (inheritedFamily) {
        fontInfo.fontFamily = inheritedFamily;
        fontInfo.inherited = true;
      }
    }

    // Only add if we have a font family (either specified or inherited)
    if (fontInfo.fontFamily) {
      fontData.add(JSON.stringify(fontInfo));
    }
  };

  // Check base styles
  if (element.styles) {
    const fontInfo = extractFontFromStyles(element.styles);
    processFontInfo(fontInfo, path);
  }

  // Check selected styles
  if (element.selectedStyles) {
    const fontInfo = extractFontFromStyles(element.selectedStyles);
    processFontInfo(fontInfo, path);
  }

  // Check focus styles
  if (element.focusStyles) {
    const fontInfo = extractFontFromStyles(element.focusStyles);
    processFontInfo(fontInfo, path);
  }

  // Check hover styles
  if (element.hoverStyles) {
    const fontInfo = extractFontFromStyles(element.hoverStyles);
    processFontInfo(fontInfo, path);
  }

  // Recursively scan nested elements
  Object.entries(element).forEach(([key, value]) => {
    if (
      typeof value === 'object' &&
      value !== null &&
      !Array.isArray(value) &&
      key !== 'styles' &&
      key !== 'selectedStyles' &&
      key !== 'focusStyles' &&
      key !== 'hoverStyles'
    ) {
      scanElementForFonts(value, fontData, `${path}.${key}`, tokens);
    }
  });
};

/**
 * Normalize font weight to Google Fonts standard weights
 * @param {number|string} weight - Font weight value
 * @returns {number} Normalized weight
 */
const normalizeFontWeight = weight => {
  const numWeight = parseInt(weight, 10);

  // Map common weight names/values to standard weights
  const weightMap = {
    100: 100, // Thin
    200: 200, // Extra Light
    300: 300, // Light
    400: 400, // Regular/Normal
    500: 500, // Medium
    600: 600, // Semi Bold
    700: 700, // Bold
    800: 800, // Extra Bold
    900: 900, // Black
  };

  // Round to nearest standard weight
  if (numWeight <= 150) return 100;
  if (numWeight <= 250) return 200;
  if (numWeight <= 350) return 300;
  if (numWeight <= 450) return 400;
  if (numWeight <= 550) return 500;
  if (numWeight <= 650) return 600;
  if (numWeight <= 750) return 700;
  if (numWeight <= 850) return 800;
  return 900;
};

/**
 * Extract font weights declared inline in seller content (product/page
 * descriptions, variant descriptions, HTML blocks). These live in `style`
 * attributes as CSS (`font-weight: 900`), so they never show up in the theme
 * tokens/elements scan — but the live page still has to load those faces or
 * the browser falls back to the nearest loaded weight.
 * Works on raw HTML as well as JSON-serialized content (quotes may be escaped
 * but the `font-weight:` declaration itself survives stringification intact).
 * @param {string} content - HTML or JSON-serialized content to scan
 * @returns {number[]} Normalized weights found in the content
 */
export const extractContentFontWeights = content => {
  if (!content || typeof content !== 'string') return [];

  const weights = new Set();
  const regex = /font-weight:\s*(\d+|bold|normal)/gi;

  let match;
  while ((match = regex.exec(content)) !== null) {
    const raw = match[1].toLowerCase();
    const numeric = raw === 'bold' ? 700 : raw === 'normal' ? 400 : parseInt(raw, 10);
    if (!isNaN(numeric)) {
      weights.add(normalizeFontWeight(numeric));
    }
  }

  return Array.from(weights);
};

/**
 * Analyze theme data to extract all required fonts
 * @param {Object} theme - Theme object containing tokens and elements
 * @param {Object} [options]
 * @param {string} [options.content] - Seller content (HTML or JSON-serialized)
 *   scanned for inline font-weight declarations; found weights are added to
 *   every collected font family so descriptions render at their real weight
 * @returns {Object} Analysis result with fonts and their specifications
 */
export const analyzeThemeFonts = (theme, { content } = {}) => {
  if (!theme) {
    return {
      fonts: [],
      fontFamilies: [],
      hasCustomFonts: false,
    };
  }

  const fontDataSet = new Set();

  // The checkout CSS derives extra weights from the token weights (see
  // CheckoutCSS.js and checkout-defaults.generated.css): field labels,
  // buttons and prices use body weight + 100, card/ticket names use body
  // weight + 200, and form headings use heading weight - 100. Those faces
  // must be loaded too, otherwise the browser falls back to the nearest
  // loaded weight and the page renders lighter/bolder than the theme.
  const BODY_WEIGHT_DELTAS = [0, 100, 200];
  const HEADING_WEIGHT_DELTAS = [0, -100];

  const addTokenFont = (fontFamily, weight, deltas, source) => {
    const baseWeight = parseInt(weight, 10);
    deltas.forEach(delta => {
      fontDataSet.add(
        JSON.stringify({
          fontFamily,
          fontWeight: normalizeFontWeight(Math.min(900, Math.max(100, baseWeight + delta))),
          isItalic: false,
          source,
        }),
      );
    });
  };

  // Extract fonts from theme tokens
  if (theme.tokens) {
    const mainFont = theme.tokens.fontMain?.family;

    // Main font (inherited by heading/body when they are unset)
    if (mainFont && mainFont !== 'default') {
      addTokenFont(
        mainFont,
        theme.tokens.fontHeading?.weight || 700,
        HEADING_WEIGHT_DELTAS,
        'main',
      );
      addTokenFont(mainFont, theme.tokens.fontBody?.weight || 400, BODY_WEIGHT_DELTAS, 'main');
    }

    // Heading font
    if (theme.tokens.fontHeading?.family && theme.tokens.fontHeading.family !== 'default') {
      addTokenFont(
        theme.tokens.fontHeading.family,
        theme.tokens.fontHeading.weight || 400,
        HEADING_WEIGHT_DELTAS,
        'heading',
      );
    }

    // Body font
    if (theme.tokens.fontBody?.family && theme.tokens.fontBody.family !== 'default') {
      addTokenFont(
        theme.tokens.fontBody.family,
        theme.tokens.fontBody.weight || 400,
        BODY_WEIGHT_DELTAS,
        'body',
      );
    }
  }

  // Extract fonts from all element styles
  if (theme.elements) {
    scanElementForFonts(theme.elements, fontDataSet, 'elements', theme.tokens);
  }

  // Process collected font data
  const fontSpecs = Array.from(fontDataSet).map(fontStr => JSON.parse(fontStr));

  // Group fonts by family and collect all weights/styles
  const fontFamilyMap = new Map();

  fontSpecs.forEach(({ fontFamily, fontWeight, isItalic, source }) => {
    if (!fontFamily) return;

    if (!fontFamilyMap.has(fontFamily)) {
      fontFamilyMap.set(fontFamily, {
        family: fontFamily,
        weights: new Set(),
        hasItalic: false,
        hasRegular: false,
        sources: new Set(),
      });
    }

    const fontSpec = fontFamilyMap.get(fontFamily);

    if (fontWeight) {
      fontSpec.weights.add(normalizeFontWeight(fontWeight));
    } else {
      // Add default weights if none specified
      fontSpec.weights.add(400);
    }

    if (isItalic) {
      fontSpec.hasItalic = true;
    } else {
      fontSpec.hasRegular = true;
    }

    if (source) {
      fontSpec.sources.add(source);
    }
  });

  // Inline font-weights in seller content apply to whichever theme font the
  // content inherits; we can't tell heading from body at this level, so add
  // them to every collected family (a couple of extra faces at most).
  const contentWeights = extractContentFontWeights(content);
  if (contentWeights.length > 0) {
    fontFamilyMap.forEach(fontSpec => {
      contentWeights.forEach(weight => fontSpec.weights.add(weight));
      fontSpec.hasRegular = true;
    });
  }

  // Convert to final format
  const fonts = Array.from(fontFamilyMap.values()).map(fontSpec => ({
    family: fontSpec.family,
    weights: Array.from(fontSpec.weights).sort((a, b) => a - b),
    hasItalic: fontSpec.hasItalic,
    hasRegular: fontSpec.hasRegular,
    sources: Array.from(fontSpec.sources),
  }));

  return {
    fonts,
    fontFamilies: fonts.map(f => f.family),
    hasCustomFonts: fonts.length > 0,
  };
};

/**
 * Check if a font family is likely a Google Font
 * @param {string} fontFamily - Font family name
 * @returns {boolean} True if likely a Google Font
 */
export const isLikelyGoogleFont = fontFamily => {
  if (!fontFamily || typeof fontFamily !== 'string') return false;

  // Convert to lowercase for comparison
  const lowerFamily = fontFamily.toLowerCase().trim();

  // System fonts that should not be loaded from Google Fonts
  const systemFonts = [
    'inter',
    '-apple-system',
    'blinkmacsystemfont',
    'segoe ui',
    'roboto',
    'oxygen-sans',
    'ubuntu',
    'cantarell',
    'helvetica neue',
    'sans-serif',
    'serif',
    'monospace',
    'cursive',
    'fantasy',
    'arial',
    'helvetica',
    'times',
    'times new roman',
    'courier',
    'courier new',
    'georgia',
    'verdana',
    'tahoma',
    'trebuchet ms',
    'comic sans ms',
    'impact',
    'palatino',
    'garamond',
    'bookman',
    'avant garde',
    'default',
  ];

  return !systemFonts.includes(lowerFamily);
};

export default analyzeThemeFonts;
